import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from '@atoms';
import { DateSelectingModal, ListSelectingModal } from '@molecules';
import { Onboarding } from '@templates';
import { Flex, Typography } from '@mixins';
import { getDateLabel, formatTimeValue, getTimeList } from '@helpers/time';
import { dayjs } from '@utils';
import { theme } from '@styles';
import { routes } from '@constants';
import { ONBOARDING_STEPS } from '@constants/common';
import { updateCompanyOnboarding } from '@store/company/duck';

import ArrivalDateOnboardingImage from '@assets/images/ArrivalDateOnboarding.png';

const ArrivalDateOnboarding = ({ direction }) => {
  const { currentLocation } = useSelector(store => store.locationStore);
  const { onboardingState } = useSelector(store => store.companyStore);

  const initialDate = useMemo(
    () =>
      dayjs(onboardingState?.originalMoveInDate ?? new Date().toISOString())
        .set('hours', 11)
        .set('minutes', 0),
    [onboardingState?.originalMoveInDate, currentLocation?.timeZone],
  );

  const [selectedOption, setSelectedOption] = useState(null);
  const [date, setDate] = useState(onboardingState?.arrivalDate ? dayjs(onboardingState?.arrivalDate) : null);
  const [time, setTime] = useState(
    onboardingState?.arrivalDate ? dayjs(onboardingState.arrivalDate).format('HH:mm') : null,
  );

  useEffect(() => {
    if (onboardingState?.finishedSteps?.includes(ONBOARDING_STEPS.CONFIRM_ARRIVAL_DATE)) {
      setSelectedOption(0);
    }
    if (onboardingState?.arrivalDate) {
      setDate(dayjs(onboardingState.arrivalDate).set('hours', 11).set('minutes', 0));
      setTime(dayjs(onboardingState.arrivalDate).format('HH:mm'));

      setSelectedOption(1);
    }
    // timeZone is a dependency because dayjs renders in the location's timezone, which is only
    // set once the location request resolves — without it the state keeps the browser's timezone
  }, [onboardingState?.arrivalDate, onboardingState?.finishedSteps, currentLocation?.timeZone]);

  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);

  const pickersRef = useRef();

  const isSelectionIncomplete = (!!date && !time) || (!date && !!time);
  const isChangeRequestIncomplete = selectedOption === 1 && (!date || !time);

  useEffect(() => {
    if (!isDateModalOpen && !isTimeModalOpen) {
      return undefined;
    }

    const handleOutsideClick = event => {
      if (pickersRef.current && !pickersRef.current.contains(event.target)) {
        setIsDateModalOpen(false);
        setIsTimeModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isDateModalOpen, isTimeModalOpen]);

  const times = useMemo(
    () => getTimeList(date ?? dayjs()).filter(timeSlot => timeSlot.$H >= 11 && timeSlot.$H <= 14),
    [date],
  );

  // Time is stored as a canonical 'HH:mm' value; the location's format only affects how it is displayed
  const timeOptions = useMemo(
    () =>
      times.map(timeline => {
        const value = timeline.format('HH:mm');

        return { label: formatTimeValue(value, currentLocation?.timeFormat), value };
      }),
    [currentLocation?.timeFormat, times],
  );

  const timeLabel = useMemo(
    () => formatTimeValue(time, currentLocation?.timeFormat),
    [currentLocation?.timeFormat, time],
  );

  const initialTimeLabel = useMemo(
    () => formatTimeValue(initialDate.format('HH:mm'), currentLocation?.timeFormat),
    [currentLocation?.timeFormat, initialDate],
  );

  const dispatch = useDispatch();
  const history = useHistory();

  const handleOptionSelect = useCallback(
    option => () => {
      setSelectedOption(option);
    },
    [],
  );

  const handleClear = useCallback(() => {
    setDate(null);
    setTime(null);
    setIsDateModalOpen(false);
    setIsTimeModalOpen(false);
  }, []);

  const handleDateModalChangeState = useCallback(() => {
    setIsDateModalOpen(prevValue => {
      if (!prevValue) {
        setIsTimeModalOpen(false);
      }
      return !prevValue;
    });
  }, []);

  const handleTimeModalChangeState = useCallback(() => {
    setIsTimeModalOpen(prevValue => {
      if (!prevValue) {
        setIsDateModalOpen(false);
      }

      return !prevValue;
    });
  }, []);

  const handleDateSelect = useCallback(
    selectedDate => {
      const offset = dayjs().$offset ?? 0;

      setDate(
        dayjs(selectedDate)
          .add(-(offset + new Date(selectedDate).getTimezoneOffset()), 'minutes')
          .add(dayjs().$u ? -new Date(selectedDate).getTimezoneOffset() : 0, 'minute'),
      );

      setIsDateModalOpen(false);
    },
    [dayjs().$offset, dayjs().$u, new Date().getTimezoneOffset()],
  );

  const handleTimeSelect = newTime => () => {
    handleTimeModalChangeState();
    setTime(newTime);
  };

  const isWeekendDisabled = useCallback(
    ({ date: tileDate, view }) => {
      if (view !== 'month') {
        return false;
      }

      const dayOfWeek = tileDate.getDay();
      const isIsraelLocation = currentLocation?.country === 'Israel';

      // Israel weekend: Friday (5) & Saturday (6); elsewhere: Saturday (6) & Sunday (0)
      return isIsraelLocation ? dayOfWeek === 5 || dayOfWeek === 6 : dayOfWeek === 0 || dayOfWeek === 6;
    },
    [currentLocation?.country],
  );

  // Confirming keeps the original date (null = no change requested); an unanswered step must not clear a saved request
  const getArrivalDatePayload = () => {
    if (selectedOption === 0) {
      return { arrivalDate: null };
    }

    if (selectedOption === 1 && date && time) {
      return {
        arrivalDate: date.set('hours', time.split(':')[0]).set('minutes', time.split(':')[1]).toISOString(),
      };
    }

    return {};
  };

  const handleUpdateDate = () => {
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.CONFIRM_ARRIVAL_DATE,
        ...getArrivalDatePayload(),
      }),
    );
  };

  const handleBack = () => {
    handleUpdateDate();

    history.push(routes.customizeSpaceOnboarding, { direction: 'back' });
  };

  const handleNavigateNext = () => {
    history.push(routes.scheduleOrientationOnboarding, { direction: 'forward' });
  };

  const handleNext = () => {
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.CONFIRM_ARRIVAL_DATE,
        nextStep: ONBOARDING_STEPS.SCHEDULE_ORIENTATION,
        ...getArrivalDatePayload(),
      }),
    );

    handleNavigateNext();
  };

  const options = useMemo(
    () => (
      <>
        <Flex
          backgroundColor={selectedOption === 0 ? theme.color.mintMinded[300] : theme.color.white}
          pointer
          onClick={handleOptionSelect(0)}
          flexDirection="column"
          p="8px"
          mb={20}
          width="100%"
          maxWidth="517px"
          borderRadius="4px"
          border={`1px solid ${selectedOption === 0 ? theme.color.mintMinded[300] : theme.color.mintMinded[900]}`}>
          <Typography fontSize={[14, 16, 18]} variant="diatype-300">
            Confirm Arrival on
          </Typography>
          <Typography fontSize={[14, 16, 18]} variant="diatype-500">{`${dayjs(initialDate).format('dddd')},
            ${dayjs(initialDate).format('MMMM D')} at ${initialTimeLabel}`}</Typography>
        </Flex>
        <Flex
          backgroundColor={selectedOption === 1 ? theme.color.mintMinded[300] : theme.color.white}
          pointer
          onClick={handleOptionSelect(1)}
          flexDirection="column"
          p="8px"
          mb={20}
          width="100%"
          maxWidth="517px"
          borderRadius="4px"
          border={`1px solid ${selectedOption === 1 ? theme.color.mintMinded[300] : theme.color.mintMinded[900]}`}>
          <Typography fontSize={[14, 16, 18]} variant="diatype-300">
            Request a Different{' '}
          </Typography>
          <Typography fontSize={[14, 16, 18]} variant="diatype-500">
            Date/Time
          </Typography>
        </Flex>
      </>
    ),
    [selectedOption, initialDate, initialTimeLabel, time],
  );

  return (
    <Onboarding
      onSaveAndContinueLater={handleUpdateDate}
      isNextButtonDisabled={selectedOption === null || isChangeRequestIncomplete}
      onNext={handleNext}
      direction={direction}
      title="Confirm Move-in Date"
      image={ArrivalDateOnboardingImage}
      showNavigationItems
      currentStep={4}
      onBack={handleBack}>
      <Typography
        variant="diatype-300"
        mb={30}
        fontSize={[14, 16, 20]}
        color={theme.color.mintMinded[900]}
        lineHeight="1.6">
        Your move-in is scheduled for {dayjs(initialDate).format('dddd')},{' '}
        <Typography variant="diatype-500" as="span">
          {dayjs(initialDate).format('MMMM D')}, starting at {initialTimeLabel}.
        </Typography>
        <br />
        Please confirm or request a change.
      </Typography>
      {options}
      {selectedOption === 1 && (
        <Flex flexDirection="column" mb={50}>
          <Flex ref={pickersRef} position="relative" flexDirection="row">
            <Flex mr={['30px', '50px', '50px']} marginBottom="0px">
              <Dropdown
                onDropdownClick={handleDateModalChangeState}
                value={date ? getDateLabel(date) : null}
                defaultValue="Date"
                alignSelf="end"
                title="Date"
                position="relative"
                width={['140px', '200px', '200px']}>
                {isDateModalOpen && (
                  <DateSelectingModal
                    minDate={initialDate.$d}
                    canBeClosed={false}
                    hasSelectingButton={false}
                    onSelect={handleDateSelect}
                    onModalClose={handleDateModalChangeState}
                    onClear={handleClear}
                    clearLabel="Clear"
                    calendarDate={date?.$d ?? null}
                    tileDisabled={isWeekendDisabled}
                    type="month"
                    topPosition={-345}
                  />
                )}
              </Dropdown>
            </Flex>
            <Flex flexDirection="column" width={['150px', '200px', '200px']} alignSelf="end">
              <Dropdown
                onDropdownClick={handleTimeModalChangeState}
                value={timeLabel}
                defaultValue="Time"
                alignSelf="end"
                title="Time"
                position="relative"
                width="100%">
                {isTimeModalOpen && (
                  <ListSelectingModal
                    canBeClosed={false}
                    hasSelectingButton={false}
                    items={timeOptions}
                    selectedItem={time}
                    onSelect={handleTimeSelect}
                    onModalClose={handleTimeModalChangeState}
                    onClear={handleClear}
                    clearLabel="Clear"
                    topPosition={-310}
                  />
                )}
              </Dropdown>
            </Flex>
          </Flex>
          <Flex mt="8px" minHeight="18px">
            {isSelectionIncomplete && (
              <Typography fontSize={12} variant="diatype-400" color={theme.color.red}>
                *Please select both a date and a time
              </Typography>
            )}
          </Flex>
        </Flex>
      )}
    </Onboarding>
  );
};

ArrivalDateOnboarding.propTypes = {
  direction: string,
};

ArrivalDateOnboarding.defaultProps = {
  direction: null,
};

export default ArrivalDateOnboarding;
